import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import { FaSave, FaChevronLeft, FaEdit, FaTrash } from 'react-icons/fa';
import { IoAddSharp } from 'react-icons/io5';
import { ClipLoader } from 'react-spinners';
import { useSelector } from 'react-redux';
import { getName } from '../../../Redux/selecters';
import LeaseApiService from '../../services/apiService';
import { endPoint } from '../../EndPoints/endPoint';
import SaleVoucherItemForm from './saleVoucherItemForm';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Same resolution order used by generalInvoiceReceipt.js/header.js's FY selector -
// whatever the user explicitly picked takes priority over the isActive flag.
// The stored object's own `name` is a human-readable description ("From 01 Jan,
// 2026 To 31 Dec, 2026"), not the short code the backend's FinancialYear column
// expects - leaseRevenue.js's already-working posting flow sends `fy.id`
// instead, so that's read first here too rather than reconstructing a string
// ourselves (which only re-guesses a format real data already has).
const financialYearCode = (fy) => {
  if (fy?.id) return String(fy.id);
  const startDate = fy?.yearFrom || fy?.YearFrom || fy?.fromDate;
  const year = startDate ? new Date(startDate).getFullYear() : NaN;
  return Number.isNaN(year) ? null : `FY ${year}`;
};
const resolveActiveFinancialYear = () => {
  const savedFY = JSON.parse(localStorage.getItem('selectedFinancialYear') || 'null');
  const savedCode = financialYearCode(savedFY);
  if (savedCode) return savedCode;
  const financialYears = JSON.parse(localStorage.getItem('financialYear') || '[]') || [];
  const active = financialYears?.find((fy) => fy.isActive);
  return financialYearCode(active) || `FY ${new Date().getFullYear()}`;
};

const CUSTOMER_TYPES = [
  { label: 'All', value: 0 },
  { label: 'Tenant', value: 1 },
  { label: 'Landlord', value: 2 },
  { label: 'Freehold', value: 3 },
];

// Verified against the real, working Pro360 mobile app source (Sale Voucher
// module) rather than guessed - the /dropdowns response's own field names are
// plain lowercase (cc1/cc2/.../vouchers) since the backend builds them that
// way itself, but everything returned through Dapper DataTable/Expando
// conversion (voucher details, taxes, accounts, customers) follows this app's
// usual "only the first letter gets lowercased" JSON casing rule - confirmed
// via the mobile app's own fromJson mappings (e.g. PDCAcCode -> pDCAcCode).
const SaleVoucherForm = ({ mode = 'create', voucherNo, dropdowns, onClose, onSaved }) => {
  const apiService = new LeaseApiService();
  const userName = useSelector(getName);
  const isEdit = mode === 'edit';
  const viewOnly = mode === 'view';
  const activeFY = resolveActiveFinancialYear();

  const [loading, setLoading] = useState(isEdit || viewOnly);
  const [saving, setSaving] = useState(false);
  const [voucherDetails, setVoucherDetails] = useState(null);
  const [loadCOA, setLoadCOA] = useState(false);
  const [customerType, setCustomerType] = useState(CUSTOMER_TYPES[0]);
  const [customer, setCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [applySameCostCenters, setApplySameCostCenters] = useState(true);
  const [items, setItems] = useState([]);
  const [itemModal, setItemModal] = useState({ show: false, index: null, item: null });
  // New attachments picked in this session (not yet uploaded) and attachments
  // already saved on the voucher (loaded on edit). Deleted existing ones are
  // tracked separately since the backend needs their recordId to remove them.
  const [newAttachments, setNewAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletedAttachments, setDeletedAttachments] = useState([]);

  const [header, setHeader] = useState({
    transactionMethod: null,
    invoiceNo: '',
    invoiceDate: new Date(),
    orderNo: '',
    orderDate: new Date(),
    dueDate: new Date(),
    refNo: '',
    postingDate: new Date(),
    currency: null,
    conversionRate: '1.0000',
    amountDebit: '',
    taxCode: null,
    taxInclusive: false,
    costCenter1: null,
    costCenter2: null,
    costCenter3: null,
    costCenter4: null,
    narration: '',
  });

  const typeOptions = dropdowns?.types?.map((t) => ({ value: t?.id, label: t?.name })) || [];
  const taxOptions = dropdowns?.taxes?.map((t) => ({ value: t?.recordId, label: t?.description, rate: t?.rate, raw: t })) || [];
  const currencyOptions = dropdowns?.currencies?.map((c) => ({ value: c?.id, label: c?.name || c?.id })) || [];
  const cc1Options = dropdowns?.cc1?.map((c) => ({ value: c?.id, label: c?.name })) || [];
  const cc2Options = dropdowns?.cc2?.map((c) => ({ value: c?.id, label: c?.name })) || [];
  const cc3Options = dropdowns?.cc3?.map((c) => ({ value: c?.id, label: c?.name })) || [];
  const cc4Options = dropdowns?.cc4?.map((c) => ({ value: c?.id, label: c?.name })) || [];
  const customerOptions = (loadCOA ? dropdowns?.accounts : dropdowns?.customers)?.map((c) => ({ value: c?.id, label: c?.name, taxCode: c?.taxCode })) || [];
  const accountsForItems = dropdowns?.accounts || [];

  useEffect(() => {
    if (isEdit || viewOnly) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await apiService.getApiParamater('SALE_VOUCHER_DETAILS', voucherNo);
      const data = res?.data?.data;
      if (!data) {
        toast.error('Failed to load voucher details');
        return;
      }
      setVoucherDetails(data);
      const main = data.main || {};

      const custOption = { value: main.customerNo, label: main.customerNo };
      setCustomer(custOption);
      if (main.customerNo) fetchCustomerDetails(main.customerNo);

      setHeader({
        transactionMethod: { value: main.transactionMethod, label: main.transactionMethod },
        invoiceNo: main.invoiceNo || '',
        invoiceDate: main.invoiceDate ? new Date(main.invoiceDate) : new Date(),
        orderNo: main.orderNo || '',
        orderDate: main.orderDate ? new Date(main.orderDate) : new Date(),
        dueDate: main.dueDate ? new Date(main.dueDate) : new Date(),
        refNo: main.refNo || '',
        postingDate: main.postingDate ? new Date(main.postingDate) : new Date(),
        currency: { value: main.currency, label: main.currency },
        conversionRate: main.conversionRate != null ? String(main.conversionRate) : '1.0000',
        amountDebit: main.amountDebit != null ? String(main.amountDebit) : '',
        taxCode: taxOptions.find((t) => t.value === main.taxCode) || null,
        taxInclusive: !!main.taxInclusive,
        costCenter1: main.costCenter1 ? { value: main.costCenter1, label: main.costCenter1 } : null,
        costCenter2: main.costCenter2 ? { value: main.costCenter2, label: main.costCenter2 } : null,
        costCenter3: main.costCenter3 ? { value: main.costCenter3, label: main.costCenter3 } : null,
        costCenter4: main.costCenter4 ? { value: main.costCenter4, label: main.costCenter4 } : null,
        narration: main.narration || '',
      });

      const transactions = data.transactions || [];
      setItems(
        transactions.map((t) => ({
          accountCode: t.accountCode,
          accountName: t.accountName,
          taxCode: t.taxCode,
          taxName: taxOptions.find((tx) => tx.value === t.taxCode)?.label || '',
          amountCredit: t.amountCredit,
          vat: t.vAT,
          total: t.total,
          narration: t.narration,
          costCenter1: t.costCenter1,
          costCenter2: t.costCenter2,
          costCenter3: t.costCenter3,
          costCenter4: t.costCenter4,
          vatAccountCode: t.vATAccountCode,
          stAccountCode: t.sTAccountCode,
        }))
      );

      // Field names here aren't confirmed against a live response (financeSharedService.
      // GetPostingAttachments' shape wasn't available) - picking the first present
      // variant defensively rather than guessing a single casing.
      const attachments = data.attachments || [];
      setExistingAttachments(
        attachments.map((a) => ({
          recordId: a.recordId ?? a.RecordId ?? 0,
          fileName: a.fileName ?? a.FileName ?? '',
          description: a.description ?? a.Description ?? '',
          url: a.url ?? a.fullUrl ?? a.Url ?? '',
        }))
      );
    } catch (error) {
      console.error('Error fetching sale voucher details:', error);
      toast.error('Failed to load voucher details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerNo) => {
    try {
      const params = new URLSearchParams();
      params.append('financialYear', activeFY);
      params.append('propertyName', '');
      params.append('unitNo', '');
      params.append('userType', '0');
      params.append('customerNo', customerNo);
      const res = await apiService.getApiParamater('SALE_VOUCHER_CUSTOMER_DETAILS_BY_TYPE', `?${params.toString()}`);
      const data = res?.data?.data;
      if (data?.customerNo) {
        setCustomerDetails(data);
        return data;
      }
      setCustomerDetails(null);
      return null;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return null;
    }
  };

  const handleCustomerTypeChange = async (option) => {
    setCustomerType(option);
    if (option.value !== 0 && header.costCenter1 && header.costCenter2) {
      await fetchCustomerDetailsByType(option.value);
    }
  };

  const fetchCustomerDetailsByType = async (userType) => {
    try {
      const params = new URLSearchParams();
      params.append('financialYear', activeFY);
      params.append('propertyName', header.costCenter1?.label || '');
      params.append('unitNo', header.costCenter2?.value || '');
      params.append('userType', String(userType));
      params.append('customerNo', '');
      const res = await apiService.getApiParamater('SALE_VOUCHER_CUSTOMER_DETAILS_BY_TYPE', `?${params.toString()}`);
      const data = res?.data?.data;
      if (data?.customerNo) {
        setCustomerDetails(data);
        setCustomer({ value: data.customerNo, label: data.accountName || data.customerNo });
      } else {
        setCustomerDetails(null);
        setCustomer(null);
      }
    } catch (error) {
      console.error('Error fetching customer details by type:', error);
    }
  };

  const handleCostCenter2Change = async (option) => {
    setHeader((prev) => ({ ...prev, costCenter2: option, costCenter3: null, costCenter4: null }));
    if (customerType.value !== 0 && header.costCenter1 && option) {
      await fetchCustomerDetailsByType(customerType.value);
    }
  };

  const handleCustomerChange = async (option) => {
    setCustomer(option);
    if (option?.value) {
      const data = await fetchCustomerDetails(option.value);
      if (data?.taxCode != null) {
        setHeader((prev) => ({ ...prev, taxCode: taxOptions.find((t) => t.value === data.taxCode) || prev.taxCode }));
      }
    } else {
      setCustomerDetails(null);
    }
  };

  const handleLoadCOAChange = (checked) => {
    setLoadCOA(checked);
    setCustomer(null);
    setCustomerDetails(null);
  };

  const openAddItem = () => setItemModal({ show: true, index: null, item: null });
  const openEditItem = (item, index) => setItemModal({ show: true, index, item });
  const closeItemModal = () => setItemModal({ show: false, index: null, item: null });
  const handleDeleteItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleSaveItem = (itemData) => {
    setItems((prev) => {
      if (itemModal.index != null) {
        const updated = [...prev];
        updated[itemModal.index] = itemData;
        return updated;
      }
      return [...prev, itemData];
    });
    closeItemModal();
  };

  const sumField = (field) => items.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);

  const handleAddAttachmentFile = (file) => {
    if (!file) return;
    setNewAttachments((prev) => [...prev, { id: Date.now() + Math.random(), file, description: '' }]);
  };
  const handleNewAttachmentDescriptionChange = (id, description) => {
    setNewAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, description } : a)));
  };
  const handleRemoveNewAttachment = (id) => setNewAttachments((prev) => prev.filter((a) => a.id !== id));
  const handleRemoveExistingAttachment = (recordId) => {
    const removed = existingAttachments.find((a) => a.recordId === recordId);
    if (removed) setDeletedAttachments((prev) => [...prev, removed]);
    setExistingAttachments((prev) => prev.filter((a) => a.recordId !== recordId));
  };

  const getValidations = () => {
    if (!customer?.value) {
      toast.warning('Please select a customer / debit account');
      return false;
    }
    if (!header.amountDebit || parseFloat(header.amountDebit) <= 0) {
      toast.warning('Debit amount should not be zero');
      return false;
    }
    if (items.length === 0) {
      toast.warning('You should add at least one item');
      return false;
    }
    const totalsMatch = Math.abs(sumField('total') - (parseFloat(header.amountDebit) || 0)) < 0.01;
    if (!totalsMatch) {
      toast.warning('Credit/Debit amounts do not match');
      return false;
    }
    return true;
  };

  // Uploads any newly-picked files to the finance-posting-attachments endpoint
  // (multipart, field name "files" - confirmed via the live Swagger doc) and
  // returns the {fileName, description} pairs the Create/Update payload needs.
  // Same two-step "upload raw file, then reference its returned fileName"
  // pattern already proven for Unit Master RA images.
  const uploadNewAttachments = async () => {
    const filesToUpload = newAttachments.filter((a) => a.file instanceof File);
    if (!filesToUpload.length) return [];

    const formData = new FormData();
    filesToUpload.forEach(({ file }) => formData.append('files', file));

    const token = localStorage.getItem('Token');
    const db = localStorage.getItem('DataBase');
    const response = await axios.post(endPoint.SALE_VOUCHER_ATTACHMENT_UPLOAD, formData, {
      headers: {
        accept: 'text/plain',
        apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
        Authorization: `Bearer ${token}`,
        db,
      },
    });

    const uploaded = response?.data?.result || response?.data?.data || [];
    return filesToUpload.map((item, idx) => ({
      recordId: 0,
      fileName: uploaded[idx]?.fileName || uploaded[idx]?.FileName || item.file.name,
      description: item.description || '',
    }));
  };

  const buildPayload = (uploadedAttachments = []) => {
    const conversionRate = parseFloat(header.conversionRate) || 1;

    const voucherItems = items.map((item, idx) => ({
      recordId: idx + 1,
      accountCode: item.accountCode,
      accountName: item.accountName,
      taxCode: item.taxCode || 0,
      percentage: 0,
      st: 0,
      amountCredit: item.amountCredit || 0,
      vat: item.vat || 0,
      total: item.total || 0,
      conversionRate,
      convertedAmount: (item.amountCredit || 0) * conversionRate,
      narration: item.narration || '0',
      costCenter1: applySameCostCenters ? header.costCenter1?.value || '' : item.costCenter1 || '',
      costCenter2: applySameCostCenters ? header.costCenter2?.value || '' : item.costCenter2 || '',
      costCenter3: applySameCostCenters ? header.costCenter3?.value || '' : item.costCenter3 || '',
      costCenter4: applySameCostCenters ? header.costCenter4?.value || '' : item.costCenter4 || '',
      vatAccountCode: item.vatAccountCode || '0',
      stAccountCode: item.stAccountCode || '0',
    }));

    const payload = {
      transactionMethod: header.transactionMethod?.value || 'Sale',
      serviceType: '',
      postingDate: header.postingDate,
      currency: header.currency?.value || '',
      conversionRate,
      orderNo: header.orderNo || '0',
      orderDate: header.orderDate,
      refNo: header.refNo || '0',
      invoiceNo: header.invoiceNo || '0',
      invoiceDate: header.invoiceDate,
      dueDate: header.dueDate,
      customerNo: customer?.value || '',
      customerName: customer?.label || '',
      taxInclusive: !!header.taxInclusive,
      taxCode: header.taxCode?.value || 0,
      accountCode: customerDetails?.accountCode || '',
      accountName: customerDetails?.accountName || '',
      financialYear: activeFY,
      narration: header.narration || '0',
      amountDebit: parseFloat(header.amountDebit) || 0,
      costCenter1: header.costCenter1?.value || '',
      costCenter2: header.costCenter2?.value || '',
      costCenter3: header.costCenter3?.value || '',
      costCenter4: header.costCenter4?.value || '',
      createdBy: userName,
      createdOn: new Date(),
      voucherItems,
      voucherAttachments: uploadedAttachments,
    };

    if (!isEdit) return payload;

    return {
      ...payload,
      voucherNo: voucherDetails?.main?.voucherNo,
      postingId: voucherDetails?.main?.postingId,
      voucherSerialNo: voucherDetails?.main?.voucherSerialNo,
      postingType: voucherDetails?.main?.postingType,
      receiptNo: voucherDetails?.main?.receiptNo,
      deletedAttachments: deletedAttachments.map((a) => ({ recordId: a.recordId, fileName: a.fileName, description: a.description })),
    };
  };

  const handleSave = async () => {
    if (!getValidations()) return;
    try {
      setSaving(true);
      const uploadedAttachments = await uploadNewAttachments();
      const payload = buildPayload(uploadedAttachments);
      const res = isEdit ? await apiService.put('SALE_VOUCHER_UPDATE', payload) : await apiService.post('SALE_VOUCHER_CREATE', payload);
      if (res?.data?.status) {
        toast.success(`${res?.data?.data?.voucherNo || 'Voucher'} has been ${isEdit ? 'updated' : 'created'} successfully.`);
        onSaved?.();
      } else {
        toast.error(res?.data?.data || res?.data?.message || 'Voucher not saved, please check entered data and try again');
      }
    } catch (error) {
      console.error('Error saving sale voucher:', error);
      toast.error(error?.response?.data?.data || 'Voucher not saved, please check entered data and try again');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '85vh' }}>
        <ClipLoader color="#6E5199" size={40} />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column sale-voucher-modal-form" style={{ height: '85vh' }}>
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between flex-shrink-0">
        <h5 className="mb-0">{viewOnly ? 'View Sale Voucher' : isEdit ? 'Update Sale Voucher' : 'Create Sale Voucher'}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
      </div>
      <div className="p-3" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
          <div className="form-group grid-contain">
            <div className="d-flex">
              <label className="form-label pt-1 start">Customer Type</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={CUSTOMER_TYPES}
                value={customerType}
                onChange={handleCustomerTypeChange}
                isDisabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Customer</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={customerOptions}
                value={customer}
                onChange={handleCustomerChange}
                isDisabled={viewOnly}
              />
            </div>
            {!isEdit && !viewOnly && (
              <div className="d-flex align-items-center">
                <label className="form-label pt-1 start">Load COA</label>
                <input type="checkbox" checked={loadCOA} onChange={(e) => handleLoadCOAChange(e.target.checked)} />
              </div>
            )}
            <div className="d-flex">
              <label className="form-label pt-1 start">Ac Code</label>
              <input type="text" disabled className="p-2 form-control w-75" value={customerDetails?.accountCode || ''} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Address</label>
              <input type="text" disabled className="p-2 form-control w-75" value={customerDetails?.address || ''} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Contact No</label>
              <input type="text" disabled className="p-2 form-control w-75" value={customerDetails?.contactNo || ''} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">VAT Reg No</label>
              <input type="text" disabled className="p-2 form-control w-75" value={customerDetails?.vATRegNo || ''} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Balance</label>
              <input type="text" disabled className="p-2 form-control w-75" value={Number(customerDetails?.balance || 0).toFixed(2)} />
            </div>

            <div className="d-flex">
              <label className="form-label pt-1 start">Type</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={typeOptions}
                value={header.transactionMethod}
                onChange={(o) => setHeader((prev) => ({ ...prev, transactionMethod: o }))}
                isDisabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Invoice No</label>
              <input type="text" disabled={viewOnly} className="p-2 form-control w-75" value={header.invoiceNo} onChange={(e) => setHeader((prev) => ({ ...prev, invoiceNo: e.target.value }))} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Invoice Date</label>
              <DatePicker
                selected={header.invoiceDate}
                onChange={(date) => setHeader((prev) => ({ ...prev, invoiceDate: date }))}
                dateFormat="dd-MMM-yyyy"
                className="p-2 form-control w-100"
                disabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">LPO No</label>
              <input type="text" disabled={viewOnly} className="p-2 form-control w-75" value={header.orderNo} onChange={(e) => setHeader((prev) => ({ ...prev, orderNo: e.target.value }))} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">PO Date</label>
              <DatePicker
                selected={header.orderDate}
                onChange={(date) => setHeader((prev) => ({ ...prev, orderDate: date }))}
                dateFormat="dd-MMM-yyyy"
                className="p-2 form-control w-100"
                disabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Due Date</label>
              <DatePicker
                selected={header.dueDate}
                onChange={(date) => setHeader((prev) => ({ ...prev, dueDate: date }))}
                dateFormat="dd-MMM-yyyy"
                className="p-2 form-control w-100"
                disabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Ref No</label>
              <input type="text" disabled={viewOnly} className="p-2 form-control w-75" value={header.refNo} onChange={(e) => setHeader((prev) => ({ ...prev, refNo: e.target.value }))} />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Voucher Date</label>
              <DatePicker
                selected={header.postingDate}
                onChange={(date) => setHeader((prev) => ({ ...prev, postingDate: date }))}
                dateFormat="dd-MMM-yyyy"
                className="p-2 form-control w-100"
                disabled={viewOnly}
              />
            </div>

            <div className="d-flex">
              <label className="form-label pt-1 start">Currency</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={currencyOptions}
                value={header.currency}
                onChange={(o) => setHeader((prev) => ({ ...prev, currency: o }))}
                isDisabled={viewOnly}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Conversion Rate</label>
              <input
                type="number"
                disabled={viewOnly}
                className="p-2 form-control w-75"
                value={header.conversionRate}
                onChange={(e) => setHeader((prev) => ({ ...prev, conversionRate: e.target.value }))}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Amount (Dr)</label>
              <input
                type="number"
                disabled={viewOnly}
                className="p-2 form-control w-75"
                value={header.amountDebit}
                onChange={(e) => setHeader((prev) => ({ ...prev, amountDebit: e.target.value }))}
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Tax Code</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={taxOptions}
                value={header.taxCode}
                onChange={(o) => setHeader((prev) => ({ ...prev, taxCode: o }))}
                isDisabled={viewOnly}
                isClearable
              />
            </div>
            <div className="d-flex align-items-center">
              <label className="form-label pt-1 start">Tax Inclusive</label>
              <input
                type="checkbox"
                checked={header.taxInclusive}
                disabled={viewOnly}
                onChange={(e) => setHeader((prev) => ({ ...prev, taxInclusive: e.target.checked }))}
              />
            </div>

            <div className="d-flex">
              <label className="form-label pt-1 start">Cost Center 1</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={cc1Options}
                value={header.costCenter1}
                onChange={(o) => setHeader((prev) => ({ ...prev, costCenter1: o, costCenter2: null, costCenter3: null, costCenter4: null }))}
                isDisabled={viewOnly}
                isClearable
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Cost Center 2</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={cc2Options}
                value={header.costCenter2}
                onChange={handleCostCenter2Change}
                isDisabled={viewOnly}
                isClearable
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Cost Center 3</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={cc3Options}
                value={header.costCenter3}
                onChange={(o) => setHeader((prev) => ({ ...prev, costCenter3: o }))}
                isDisabled={viewOnly}
                isClearable
              />
            </div>
            <div className="d-flex">
              <label className="form-label pt-1 start">Cost Center 4</label>
              <Select
                className="p-0 w-100"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={cc4Options}
                value={header.costCenter4}
                onChange={(o) => setHeader((prev) => ({ ...prev, costCenter4: o }))}
                isDisabled={viewOnly}
                isClearable
              />
            </div>
          </div>

          <div className="mt-3 d-flex">
            <label className="form-label pt-1 start">Narration</label>
            <textarea
              className="p-2 form-control w-100"
              rows={3}
              disabled={viewOnly}
              value={header.narration}
              onChange={(e) => setHeader((prev) => ({ ...prev, narration: e.target.value }))}
            />
          </div>

          <div className="mt-3 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              id="applySameCostCenters"
              checked={applySameCostCenters}
              disabled={viewOnly}
              onChange={(e) => setApplySameCostCenters(e.target.checked)}
            />
            <label htmlFor="applySameCostCenters" className="form-label mb-0">
              Apply same cost centers to all items
            </label>
          </div>

          <div className="mt-3">
            <label className="form-label d-block mb-2">Attachments</label>
            {!viewOnly && (
              <input
                type="file"
                multiple
                className="form-control mb-2"
                onChange={(e) => {
                  Array.from(e.target.files || []).forEach(handleAddAttachmentFile);
                  e.target.value = '';
                }}
              />
            )}
            {(existingAttachments.length > 0 || newAttachments.length > 0) && (
              <table className="table table-bordered gridshad">
                <thead className="dynoTable">
                  <tr>
                    <th>File Name</th>
                    <th>Description</th>
                    {!viewOnly && <th>Remove</th>}
                  </tr>
                </thead>
                <tbody>
                  {existingAttachments.map((a) => (
                    <tr key={`existing-${a.recordId}`}>
                      <td>
                        {a.url ? (
                          <a href={a.url} target="_blank" rel="noopener noreferrer">
                            {a.fileName}
                          </a>
                        ) : (
                          a.fileName
                        )}
                      </td>
                      <td>{a.description}</td>
                      {!viewOnly && (
                        <td>
                          <FaTrash size={14} color="#DC3545" style={{ cursor: 'pointer' }} onClick={() => handleRemoveExistingAttachment(a.recordId)} />
                        </td>
                      )}
                    </tr>
                  ))}
                  {newAttachments.map((a) => (
                    <tr key={a.id}>
                      <td>{a.file?.name}</td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={a.description}
                          onChange={(e) => handleNewAttachmentDescriptionChange(a.id, e.target.value)}
                        />
                      </td>
                      <td>
                        <FaTrash size={14} color="#DC3545" style={{ cursor: 'pointer' }} onClick={() => handleRemoveNewAttachment(a.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!viewOnly && (
            <div className="my-3">
              <div className="mainbtnSecondary flex-end gap-2 col-2" style={{ cursor: 'pointer' }} onClick={openAddItem}>
                <IoAddSharp color="#fff" className="center mt-1 mx-2" />
                <div className="center">Add Item</div>
              </div>
            </div>
          )}

          <div className="table-container mt-3" style={{ overflowX: 'auto' }}>
            <table className="table table-bordered gridshad">
              <thead className="dynoTable">
                <tr>
                  {!viewOnly && <th>Actions</th>}
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th>Credit</th>
                  <th>Tax</th>
                  <th>VAT</th>
                  <th>Total</th>
                  <th>Narration</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    {!viewOnly && (
                      <td className="text-center d-flex gap-3">
                        <FaEdit size={18} style={{ cursor: 'pointer' }} onClick={() => openEditItem(item, index)} />
                        <FaTrash size={16} color="#DC3545" style={{ cursor: 'pointer' }} onClick={() => handleDeleteItem(index)} />
                      </td>
                    )}
                    <td>{item.accountCode}</td>
                    <td>{item.accountName}</td>
                    <td>{Number(item.amountCredit || 0).toFixed(2)}</td>
                    <td>{item.taxName}</td>
                    <td>{Number(item.vat || 0).toFixed(2)}</td>
                    <td>{Number(item.total || 0).toFixed(2)}</td>
                    <td>{item.narration}</td>
                  </tr>
                ))}
                <tr>
                  {!viewOnly && <td />}
                  <td colSpan={2}>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>{sumField('amountCredit').toFixed(2)}</strong>
                  </td>
                  <td />
                  <td>
                    <strong>{sumField('vat').toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{sumField('total').toFixed(2)}</strong>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {!viewOnly && (
        <div className="p-3 border-top d-flex justify-content-start gap-3 flex-shrink-0">
          {saving ? (
            <div className="mainbtn flex-end gap-2">
              <ClipLoader color="#ffffff" className="mt-1" size={20} />
            </div>
          ) : (
            <div className="mainbtn flex-end gap-2" onClick={handleSave}>
              <FaSave color="#fff" className="center mt-1 mx-2" />
              <div className="center">{isEdit ? 'Update' : 'Save'}</div>
            </div>
          )}
          <div className="mainbtnRed flex-end gap-2" onClick={onClose}>
            <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
            <div className="center">Cancel</div>
          </div>
        </div>
      )}

      <SaleVoucherItemForm
        show={itemModal.show}
        editItem={itemModal.item}
        onClose={closeItemModal}
        onSave={handleSaveItem}
        accounts={accountsForItems}
        taxes={dropdowns?.taxes || []}
        costCenter1={dropdowns?.cc1 || []}
        costCenter2={dropdowns?.cc2 || []}
        costCenter3={dropdowns?.cc3 || []}
        costCenter4={dropdowns?.cc4 || []}
        headerCostCenters={{ cc1: header.costCenter1, cc2: header.costCenter2, cc3: header.costCenter3, cc4: header.costCenter4 }}
        taxInclusive={header.taxInclusive}
        applySameCostCenters={applySameCostCenters}
        defaultNarration={header.narration}
      />
    </div>
  );
};

export default SaleVoucherForm;
